/** Official NEMA Digital Care Agreement templates by shift / hourly package. */

import {
  AGREEMENT_CONTENT_VERSION,
  NURSE_AND_PHYSIO_TEMPLATES,
} from "./agreementTemplates/nurseAndPhysio";

export { AGREEMENT_CONTENT_VERSION };

/** Client fallback when server ref is unavailable (prefer API allocation). */
export function generateAgreementRef(now = new Date()) {
  const year = now.getFullYear();
  const serial = String(Math.floor(Math.random() * 999999) + 1).padStart(6, "0");
  return `NEMA-AGR-${year}-${serial}`;
}

export function formatAgreementRefFromSerial(year, serial) {
  const y = Number(year) || new Date().getFullYear();
  const n = Math.max(1, Math.floor(Number(serial) || 1));
  return `NEMA-AGR-${y}-${String(n).padStart(6, "0")}`;
}

export function roleContractLabel(serviceRole) {
  const role = String(serviceRole || "").toLowerCase();
  if (role === "nurses" || role === "nurse") return "NURSE";
  if (role === "physio" || role === "physiotherapist") return "PHYSIOTHERAPIST";
  if (role === "companion") return "COMPANION";
  return "CARE ATTENDANT";
}

export function caregiverRoleDisplay(serviceRole) {
  const role = String(serviceRole || "").toLowerCase();
  if (role === "nurses" || role === "nurse") return "Nurse";
  if (role === "physio" || role === "physiotherapist") return "Physiotherapist";
  if (role === "companion") return "Companion";
  return "Care Attendant";
}

const PREAMBLE =
  "This Digital Care Agreement is entered into between the Customer and the selected Care Attendant through the NEMA CareHub Marketplace. Acceptance is recorded only when you tick the acceptance checkbox and submit explicit confirmation below; reading or scrolling this document does not constitute acceptance.";

const FOOTER =
  "NEMA CareHub acts solely as a digital marketplace facilitator connecting the Customer and the Care Attendant, and is not a party to the caregiving services rendered. This is a digitally generated agreement, valid through electronic/in-app acceptance; physical signatures are not required.";

const ZERO_TOLERANCE =
  "Zero Tolerance: NEMA CareHub prohibits abuse, neglect, harassment, theft/fraud, substance use on duty, and unauthorized abandonment of duty by either party.";

function normalizeServiceRole(serviceRole) {
  const role = String(serviceRole || "").toLowerCase();
  if (role === "nurse") return "nurses";
  if (role === "physiotherapist") return "physio";
  return role;
}

function isNurseProcedureServiceId(serviceId) {
  const sid = String(serviceId || "").trim().toLowerCase();
  if (!sid || sid.startsWith("nurse_12") || sid.startsWith("nurse_24")) return false;
  return !sid.startsWith("attendant_") && !sid.startsWith("companion_") && !sid.startsWith("pt_");
}

/** Official short agreements from NEMA Care Agreement DOCX set. */
export const CARE_ATTENDANT_AGREEMENT_TEMPLATES = {
  shift_12: {
    key: "shift_12",
    contractTitle: "Care Attendant Contract — 12 Hours (Day / Night)",
    shiftLabel: "12 Hours (Day / Night)",
    includeTimeWindow: true,
    preamble: PREAMBLE,
    scopeHeading: "1. Scope of Service",
    scopeItems: [
      "Personal care: bathing, oral hygiene, grooming, dressing, diaper/toileting assistance, and skin/pressure-area care.",
      "Mobility: transfers, walking assistance, repositioning every 2–3 hours, and fall-prevention support.",
      "Nutrition: meal serving/feeding, hydration, and oral medication reminders; reporting poor appetite or swallowing difficulty.",
      "Companionship: emotional support, conversation, recreation, and memory orientation as appropriate.",
      "Observation: prompt reporting of fever, breathing difficulty, falls, behavioural change or refusal of food/fluids; no independent treatment or clinical decisions.",
      "Patient-linked household tasks: organising the patient's room, patient utensils/linen, and patient-only laundry if agreed — general housekeeping is excluded.",
      "Shift terms: duty runs for 12 continuous hours as scheduled. Day shift ends with handover to the Customer/next caregiver, with no overnight stay. Night shift requires the Care Attendant to remain alert and monitor the Beneficiary; sleeping on duty is not permitted unless the Customer approves it for stable conditions.",
      "Not included: injections, IV/infusion management, catheter/tracheostomy/oxygen procedures, wound dressing, physiotherapy, diagnosis, financial transactions, driving, or general housekeeping/childcare/pet care.",
    ],
    billingHeading: "2. Billing",
    billingTextDaily:
      "The agreed per-day salary is payable directly to the Care Attendant; NEMA CareHub is a facilitating marketplace only. Duty beyond the agreed shift requires mutual consent and separate compensation.",
    billingTextMonthly:
      "The agreed monthly salary is payable directly to the Care Attendant; NEMA CareHub is a facilitating marketplace only. Duty beyond the agreed shift requires mutual consent and separate compensation.",
    billingText:
      "The agreed salary is payable directly to the Care Attendant; NEMA CareHub is a facilitating marketplace only. Duty beyond the agreed shift requires mutual consent and separate compensation.",
    conductHeading: "3. Conduct & Responsibilities",
    conductItems: [
      "Customer: provides a safe environment, complete patient information, and necessary caregiving supplies (diapers, gloves, toiletries, consumables).",
      "Care Attendant: performs duties diligently, maintains privacy and professional conduct, and reports emergencies without unauthorized absence.",
      ZERO_TOLERANCE,
    ],
    footer: FOOTER,
  },
  live_in_24: {
    key: "live_in_24",
    contractTitle: "Care Attendant Contract — 24 Hours Live-In",
    shiftLabel: "24 Hours – Live-In",
    includeTimeWindow: false,
    preamble: PREAMBLE,
    scopeHeading: "1. Scope of Service",
    scopeItems: [
      "Personal care: bathing, oral hygiene, grooming, dressing, diaper/toileting assistance, and skin/pressure-area care.",
      "Mobility: transfers, walking assistance, repositioning roughly every 2 hours, and fall-prevention support.",
      "Nutrition: meal preparation/serving, feeding assistance, hydration, and oral medication reminders.",
      "Daily living & companionship: toileting, dressing, grooming, bed-making, and emotional support, conversation and cognitive engagement.",
      "Observation: prompt reporting of fever, breathing difficulty, falls, reduced intake, or behavioural changes; no independent diagnosis or treatment.",
      "Patient-linked household tasks: cleaning the patient's area, patient utensils/linen/laundry, and organising medicines — general housekeeping and family cooking are excluded.",
      "Live-in terms: the Care Attendant resides on-site with a private sleeping arrangement, three meals, hygiene facilities, and reasonable daily rest; 24-hour availability does not mean 24 continuous hours of active work.",
      "Not included: injections, IV medication, wound dressing, catheter/tracheostomy/oxygen procedures, physiotherapy, diagnosis, financial transactions, driving, or duties for anyone other than the registered Beneficiary.",
    ],
    billingHeading: "2. Billing",
    billingTextDaily:
      "The agreed per-day salary is payable directly to the Care Attendant; NEMA CareHub is a facilitating marketplace only. Duties beyond standard live-in scope are compensated separately by mutual agreement.",
    billingTextMonthly:
      "The agreed monthly salary is payable directly to the Care Attendant; NEMA CareHub is a facilitating marketplace only. Duties beyond standard live-in scope are compensated separately by mutual agreement.",
    billingText:
      "The agreed salary is payable directly to the Care Attendant; NEMA CareHub is a facilitating marketplace only. Duties beyond standard live-in scope are compensated separately by mutual agreement.",
    conductHeading: "3. Conduct & Responsibilities",
    conductItems: [
      "Customer: provides accurate medical/caregiving information, a safe and harassment-free environment, and the accommodation/meals/hygiene facilities specified above.",
      "Care Attendant: performs duties diligently and confidentially, follows reasonable instructions, and reports emergencies without unauthorized absence.",
      ZERO_TOLERANCE,
    ],
    footer: FOOTER,
  },
  hourly_companion: {
    key: "hourly_companion",
    contractTitle: "Care Attendant Contract — Hourly Companion / Travel Assistance",
    shiftLabel: "Hourly – Companion / Travel Assistance",
    includeTimeWindow: true,
    preamble: PREAMBLE,
    scopeHeading: "1. Scope of Service",
    scopeItems: [
      "Companionship: conversation, reading, light recreation, cognitive engagement, and accompanied walks where medically appropriate.",
      "Daily support: meal serving, feeding assistance, hydration, and reminders for oral medication (administration excluded).",
      "Travel assistance (if selected): escort to hospitals, clinics, diagnostic centres, airports, stations or approved destinations, including boarding/deboarding, wheelchair support, carrying belongings and records, and coordination during appointments until handover.",
      "Observation: the Care Attendant will monitor the Beneficiary's well-being and promptly inform the Customer of any unusual behaviour, discomfort or emergency.",
      "Duration: services are limited to the booked hours; waiting time during visits counts toward booked duration; any extension needs mutual consent.",
      "Not included: administering medicines/injections, nursing or physiotherapy procedures, driving, hospital billing/legal/financial handling, medical decision-making, or care beyond the registered Beneficiary.",
    ],
    billingHeading: "2. Billing",
    billingText:
      "Total service charges as agreed at booking are payable directly to the Care Attendant; NEMA CareHub is a facilitating marketplace only. Travel-related expenses (transport, parking, tolls, tickets) are borne by the Customer unless otherwise agreed.",
    conductHeading: "3. Conduct & Responsibilities",
    conductItems: [
      "Customer: provides accurate travel/destination details, a safe environment, and transportation where required.",
      "Care Attendant: performs duties with compassion and professionalism, maintains confidentiality, and reports emergencies immediately without unauthorized absence.",
      ZERO_TOLERANCE,
    ],
    footer: FOOTER,
  },
  hourly_hygiene: {
    key: "hourly_hygiene",
    contractTitle: "Hygiene Care Contract — Hourly",
    shiftLabel: "Hourly – Hygiene Care",
    includeTimeWindow: true,
    preamble: PREAMBLE,
    scopeHeading: "1. Scope of Service",
    scopeItems: [
      "Personal hygiene: sponge/bed/assisted bathing, oral hygiene, face wash, grooming, hair care, diaper changing and perineal hygiene, and skin/pressure-area observation.",
      "Mobility & comfort: safe repositioning, assistance with sitting/standing/short transfers, changing clothes, and bed linen change if required during the visit.",
      "Duration: duty is limited to the booked service period; extensions require mutual consent, and the Care Attendant is not obligated to stay beyond the booked time.",
      "Not included: injections, wound dressing, physiotherapy, nursing procedures or medication administration, catheter/oxygen procedures, diagnosis, financial transactions, cooking, housekeeping, childcare or pet care, unless separately booked.",
    ],
    billingHeading: "2. Billing",
    billingText:
      "Total service charges as agreed at booking are payable directly to the Care Attendant; NEMA CareHub is a facilitating marketplace only.",
    conductHeading: "3. Conduct & Responsibilities",
    conductItems: [
      "Customer: provides a safe working environment.",
      "Care Attendant: arrives on time, performs duties diligently, maintains confidentiality, and reports emergencies immediately without unauthorized absence.",
      ZERO_TOLERANCE,
    ],
    footer: FOOTER,
  },
};

export const AGREEMENT_TEMPLATES = {
  ...CARE_ATTENDANT_AGREEMENT_TEMPLATES,
  ...NURSE_AND_PHYSIO_TEMPLATES,
};

function isCompanionPackage({ packageKey, serviceId, serviceLabel } = {}) {
  const blob = `${packageKey || ""} ${serviceId || ""} ${serviceLabel || ""}`.toLowerCase();
  return /companion|travel/.test(blob);
}

/**
 * Pick official agreement template from shift + hourly package.
 * Docs: 12 Day/Night, 24 Live-in, Hourly Companion, Hourly Hygiene.
 */
export function resolveAgreementTemplateKey({
  serviceRole,
  searchShift,
  packageKey,
  serviceId,
  serviceLabel,
} = {}) {
  const role = normalizeServiceRole(serviceRole);
  const shift = String(searchShift || "").toLowerCase();
  const sid = String(serviceId || "").toLowerCase();

  if (role === "nurses") {
    if (shift === "24h" || sid.includes("24h")) return "nurse_live_in_24";
    if (shift === "12_day" || shift === "12_night" || sid.includes("12h")) return "nurse_shift_12";
    return "nurse_hourly";
  }
  if (role === "physio") {
    return "physio_hourly";
  }

  if (shift === "24h") return "live_in_24";
  if (shift === "12_day" || shift === "12_night") return "shift_12";
  if (shift === "hourly" || !shift) {
    if (role === "nurses" || isNurseProcedureServiceId(serviceId)) return "nurse_hourly";
    return isCompanionPackage({ packageKey, serviceId, serviceLabel })
      ? "hourly_companion"
      : "hourly_hygiene";
  }
  const label = String(serviceLabel || "").toLowerCase();
  if (label.includes("24") || label.includes("live")) return "live_in_24";
  if (label.includes("12") || label.includes("day") || label.includes("night")) return "shift_12";
  return isCompanionPackage({ packageKey, serviceId, serviceLabel })
    ? "hourly_companion"
    : "hourly_hygiene";
}

export function getAgreementTemplate(templateKey) {
  return AGREEMENT_TEMPLATES[templateKey] || AGREEMENT_TEMPLATES.hourly_hygiene;
}

export function shiftContractTitle(searchShift, serviceLabel = "", templateKey = null) {
  const key = templateKey || resolveAgreementTemplateKey({ searchShift, serviceLabel });
  return getAgreementTemplate(key).contractTitle;
}

export function shiftTimingLabel(searchShift, serviceLabel = "", templateKey = null) {
  const shift = String(searchShift || "").toLowerCase();
  if (shift === "12_day") return "12 Hours (Day)";
  if (shift === "12_night") return "12 Hours (Night)";
  const key = templateKey || resolveAgreementTemplateKey({ searchShift, serviceLabel });
  return getAgreementTemplate(key).shiftLabel;
}

/** @deprecated Prefer template.scopeItems — kept for older saved snapshots. */
export function dutyClausesFor({ serviceRole, searchShift, packageKey, serviceId, serviceLabel }) {
  const key = resolveAgreementTemplateKey({
    serviceRole,
    searchShift,
    packageKey,
    serviceId,
    serviceLabel,
  });
  const template = getAgreementTemplate(key);
  return template.scopeItems.map((body, index) => ({
    title: `Scope ${index + 1}`,
    body,
  }));
}

export function formatInrAmount(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return `₹${Math.round(amount).toLocaleString("en-IN")}`;
}

export function formatAadhaarDisplay(masked) {
  const raw = String(masked || "").trim();
  if (!raw) return "Not on file";
  return raw;
}

function formatTimeWindow(startTime, endTime) {
  const start = String(startTime || "").trim();
  const end = String(endTime || "").trim();
  if (!start) return "";
  if (!end || end === start) return ` (Time: ${start})`;
  return ` (Time: ${start} – ${end})`;
}

export function isHourlyAgreementTemplate(templateKey) {
  return (
    templateKey === "hourly_companion" ||
    templateKey === "hourly_hygiene" ||
    templateKey === "nurse_hourly" ||
    templateKey === "physio_hourly"
  );
}

export function buildDigitalCareAgreement({
  agreementRef,
  serviceRole,
  searchShift,
  serviceLabel,
  packageKey,
  serviceId,
  caregiverName,
  startDate,
  startTime,
  endTime,
  patientName,
  patientId,
  patientAadhaarMasked,
  serviceAddress,
  monthlySalaryInr,
  dailyRateInr,
  /** "daily" | "monthly" — for 12h/24h engagements based on booking duration. */
  salaryBillingMode = null,
  hourlyRateInr,
  visitFeeInr,
  acknowledgedAt = null,
} = {}) {
  const templateKey = resolveAgreementTemplateKey({
    serviceRole,
    searchShift,
    packageKey,
    serviceId,
    serviceLabel,
  });
  const template = getAgreementTemplate(templateKey);
  const roleLabel = roleContractLabel(serviceRole);
  const roleDisplay = caregiverRoleDisplay(serviceRole);
  const hourlyTemplate = isHourlyAgreementTemplate(templateKey);

  let contractTitle = template.contractTitle;
  const isAttendantTemplate = Boolean(CARE_ATTENDANT_AGREEMENT_TEMPLATES[templateKey]);
  if (isAttendantTemplate && roleDisplay !== "Care Attendant") {
    contractTitle = contractTitle.replace(/Care Attendant/g, roleDisplay);
  }

  const shiftLabel =
    String(searchShift || "").toLowerCase() === "12_day"
      ? "12 Hours (Day)"
      : String(searchShift || "").toLowerCase() === "12_night"
        ? "12 Hours (Night)"
        : template.shiftLabel;

  // Hourly agreements show caregiver hourly rate — never monthly/daily salary lines.
  const mode = hourlyTemplate
    ? null
    : salaryBillingMode === "daily" || salaryBillingMode === "monthly"
      ? salaryBillingMode
      : null;
  const monthly = hourlyTemplate || mode === "daily" ? NaN : Number(monthlySalaryInr);
  const daily = hourlyTemplate || mode === "monthly" ? NaN : Number(dailyRateInr);
  const hourly = Number(hourlyRateInr);
  const visit = Number(visitFeeInr);
  const hasMonthly = Number.isFinite(monthly) && monthly > 0;
  const hasDaily = Number.isFinite(daily) && daily > 0;
  const hasHourly = Number.isFinite(hourly) && hourly > 0;
  const timeWindow =
    template.includeTimeWindow !== false ? formatTimeWindow(startTime, endTime) : "";

  let billingText = template.billingText;
  if (!hourlyTemplate && mode === "daily" && template.billingTextDaily) {
    billingText = template.billingTextDaily;
  } else if (!hourlyTemplate && mode === "monthly" && template.billingTextMonthly) {
    billingText = template.billingTextMonthly;
  }

  return {
    ref: agreementRef || generateAgreementRef(),
    title: "NEMA DIGITAL CARE AGREEMENT",
    templateKey,
    contractTitle,
    caregiverName: caregiverName || "Selected caregiver",
    caregiverRole: roleDisplay,
    roleLabel,
    shiftLabel,
    shiftKey: searchShift || null,
    shiftTimingDisplay: `${shiftLabel}${
      startDate ? ` (Starts: ${String(startDate).slice(0, 10)})` : ""
    }${timeWindow}`,
    startDate: startDate || null,
    startTime: startTime || null,
    endTime: endTime || null,
    patientName: patientName || "Selected patient",
    patientId: patientId || null,
    patientAadhaarMasked: formatAadhaarDisplay(patientAadhaarMasked),
    serviceAddress: serviceAddress || "Address to be confirmed",
    preamble: template.preamble,
    scopeHeading: template.scopeHeading,
    scopeItems: [...template.scopeItems],
    billingHeading: template.billingHeading,
    billingText,
    conductHeading: template.conductHeading,
    conductItems: [...template.conductItems],
    extraSections: Array.isArray(template.extraSections)
      ? template.extraSections.map((s) => ({
          heading: s.heading,
          items: Array.isArray(s.items) ? [...s.items] : undefined,
          paragraphs: Array.isArray(s.paragraphs) ? [...s.paragraphs] : undefined,
        }))
      : [],
    importantNote: template.importantNote || null,
    agreementVersion: AGREEMENT_CONTENT_VERSION,
    agreementCreatedAt: new Date().toISOString(),
    customerAcceptedAt: acknowledgedAt || null,
    caregiverAcceptedAt: null,
    footer: template.footer,
    // Legacy fields for older UI / saved bookings
    dutyClauses: template.scopeItems.map((body, index) => ({
      title: `Scope ${index + 1}`,
      body,
    })),
    salaryBillingMode: mode,
    dailyRateInr: hasDaily ? Math.round(daily) : null,
    monthlySalaryInr: hasMonthly ? Math.round(monthly) : null,
    hourlyRateInr: hourlyTemplate && hasHourly ? Math.round(hourly) : null,
    visitFeeInr: Number.isFinite(visit) && visit > 0 ? Math.round(visit) : null,
    // Platform activation deposit removed from all shift-type agreements.
    advanceDepositPct: null,
    advanceDepositInr: null,
    liabilityCoverInr: 500000,
    acknowledgedAt,
  };
}
